import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import BackButton from '../../../shared/components/back-button/BackButton';
import ServiceBasicsEditorPage from './ServiceBasicsEditorPage';
import { fetchCompanyStaff } from '../../../shared/booking/service-staff/serviceStaffRouting';
import { createService, listServices } from '../../../api/companyRecords';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { entityAssignedToLocation, normalizeLocationIds } from '../../../locations/locationTypes';
import { buildServiceSlugFields } from '../utils/serviceSlug';
import { useTutorial } from '../../tutorial/TutorialContext';
import '../styles/services.css';
import './service-details-v2.css';

const SETTINGS_SERVICES_PATH = '/app/overview/settings';

export default function ServiceCreatePage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [searchParams] = useSearchParams();
  const { companyData } = useAuth();
  const { setNotification } = useNotification();
  const { markStepComplete } = useTutorial();
  const { activeLocations, showLocationStep } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    activeOnly: true,
  });

  const contextLocationId =
    searchParams.get('locationId') ||
    routerLocation.state?.locationId ||
    '';
  const contextLocation = useMemo(
    () => activeLocations.find((entry) => entry.id === contextLocationId) || null,
    [activeLocations, contextLocationId]
  );
  const defaultLocationIds = useMemo(() => {
    if (contextLocationId) return [contextLocationId];
    if (activeLocations.length === 1) return [activeLocations[0].id];
    return activeLocations.map((entry) => entry.id);
  }, [contextLocationId, activeLocations]);

  const [staffOptions, setStaffOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadStaffOptions = async () => {
      if (!companyData?.id) {
        setStaffOptions([]);
        return;
      }

      try {
        const nextStaffOptions = await fetchCompanyStaff({
          companyId: companyData.id,
          ownerId: companyData?.ownerId || companyData?.createdBy,
        });
        setStaffOptions(nextStaffOptions);
      } catch (error) {
        console.error('Error loading staff options:', error);
      }
    };

    loadStaffOptions();
  }, [companyData?.id, companyData?.ownerId, companyData?.createdBy]);

  const scopedStaffOptions = useMemo(() => {
    if (!showLocationStep || !contextLocationId) {
      return staffOptions;
    }

    return staffOptions.filter((member) => entityAssignedToLocation(member, contextLocationId));
  }, [staffOptions, showLocationStep, contextLocationId]);

  const backToManageServices = () => {
    navigate(SETTINGS_SERVICES_PATH, {
      state: {
        section: 'services-categories',
        parent: 'services-categories',
        servicesTab: 'services',
        ...(contextLocationId ? { selectedLocationId: contextLocationId } : {}),
      },
    });
  };

  const handleCreateService = async (basics) => {
    if (!companyData?.id) return;

    setSaving(true);
    try {
      const existingServices = (await listServices({ companyId: companyData.id }))
        .filter((entry) => !entry._init);

      const slugFields = buildServiceSlugFields(basics.name, existingServices);
      const locationIds = normalizeLocationIds(basics.locationIds).length > 0
        ? normalizeLocationIds(basics.locationIds)
        : defaultLocationIds;

      const serviceData = {
        name: basics.name,
        description: basics.description || '',
        duration: basics.duration,
        bufferTime: basics.bufferTime ?? 0,
        price: basics.price,
        imageUrl: basics.imageUrl || '',
        employeeIds: basics.employeeIds || [],
        locationIds,
        ...slugFields,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        addOns: [],
        packages: [],
        addOnsCount: 0,
        packagesCount: 0,
      };

      if (basics.calendarColor) {
        serviceData.calendarColor = basics.calendarColor;
      }

      await createService({ companyId: companyData.id, service: serviceData });
      markStepComplete(3);
      setNotification({ open: true, message: 'Service created successfully', severity: 'success' });
      navigate(`/app/overview/services/${slugFields.slug}`);
    } catch (error) {
      console.error('Error creating service:', error);
      setNotification({
        open: true,
        message: `Error creating service: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="svc-page sd2-editPage">
      <div className="sd2-editTopbar">
        <BackButton label="Back to services" className="back-btn--flush" onClick={backToManageServices} />
      </div>

      <ServiceBasicsEditorPage
        companyId={companyData?.id}
        busy={saving}
        staffOptions={scopedStaffOptions}
        locationOptions={activeLocations}
        defaultLocationIds={defaultLocationIds}
        contextLocationName={contextLocation?.name || ''}
        onCancel={backToManageServices}
        onSave={handleCreateService}
      />
    </div>
  );
}
